import React, { useState, useEffect } from 'react';
import { Switch, Text, Group, Paper, Notification } from '@mantine/core';
import { IconMail, IconAlertCircle } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useStore } from '../store/Store';
import { subscribeToNewsletter, unsubscribeFromNewsletter } from '../Api';

interface NewsletterSubscriptionProps {
  color: string;
}

export function NewsletterSubscription({ color }: NewsletterSubscriptionProps) {
  const { t } = useTranslation();
  const userProfile = useStore((state) => state.userProfile);
  const setUserProfile = useStore((state) => state.setUserProfile);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setIsSubscribed(userProfile.newsletterSubscribed || false);
  }, [userProfile.newsletterSubscribed]);

  const handleNewsletterToggle = async (checked: boolean) => {
    if (!userProfile._id || !userProfile.email) {
      setError('User information is missing');
      return;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      if (checked) {
        // Subscribe to newsletter
        await subscribeToNewsletter(userProfile._id, userProfile.email);
        setUserProfile({ newsletterSubscribed: true });
        setIsSubscribed(true);
      } else {
        // Unsubscribe from newsletter
        await unsubscribeFromNewsletter(userProfile._id, userProfile.email);
        setUserProfile({ newsletterSubscribed: false });
        setIsSubscribed(false);
      }
    } catch (error) {
      console.error('Error updating newsletter subscription:', error);
      setError('Error updating newsletter subscription');
      // Revert the switch state on error
      setIsSubscribed(!checked);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Paper shadow="sm" radius="lg" p="md" style={{ border: `1px solid ${color}20` }}>
      {error && (
        <Notification
          icon={<IconAlertCircle size={16} />}
          color="red"
          title={t('common.error')}
          mb="md"
          withCloseButton
          onClose={() => setError(null)}
        >
          {error}
        </Notification>
      )}

      <Group justify="space-between" align="flex-start" gap="md">
        <div style={{ flex: 1 }}>
          <Group gap="sm" mb="xs">
            <IconMail size={18} color={color} />
            <Text size="sm" fw={500}>
              {t('account.newsletter.subscribe')}
            </Text>
          </Group>
          
          <Text size="xs" color="dimmed" style={{ marginLeft: '26px' }}>
            {t('account.newsletter.description')}
          </Text>
        </div>
        
        <Switch
          checked={isSubscribed}
          onChange={(event) => handleNewsletterToggle(event.currentTarget.checked)}
          disabled={isLoading}
          color={color}
          size="md"
        />
      </Group>
    </Paper>
  );
}
